'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IMegaMenuNavigationItemColumn } from '../interfaces';
import { cn } from '@/app/lib';
import ListMegaMenu from '../../MegaMenu/List/ListMegaMenu';
import GridMegaMenu from '../../MegaMenu/Grid/GridMegaMenu';
import { getActivePathFromLink } from '@/app/lib';
import { NavigationItem } from './NavigationItem';

const MARKER_ANIMATION_DELAY = 100;

interface INavigationBarProps {
  navigationItems: IMegaMenuNavigationItemColumn[];
  className?: string;
  activePath?: string;
  onActivePathChange?: (newPath: string) => void;
}

const norm = (p?: string | null) => {
  if (!p) return '';
  const u = p.split('#')[0].split('?')[0];
  return u !== '/' ? u.replace(/\/+$/, '') : '/';
};

const pathMatches = (itemPath: string, actPath?: string) => {
  if (!itemPath || !actPath) return false;
  const a = norm(actPath);
  const i = norm(itemPath);
  if (i === '/') return a === '/';
  return a === i || a.startsWith(`${i}/`);
};

const isSameByKey = (a?: IMegaMenuNavigationItemColumn, b?: IMegaMenuNavigationItemColumn) =>
  Boolean(a?._key && b?._key && a._key === b._key);

const NAV_ITEM_PADDING = 6; // px

export const NavigationBar = ({ navigationItems, className, activePath, onActivePathChange }: INavigationBarProps) => {
  const [expandedItem, setExpandedItem] = useState<IMegaMenuNavigationItemColumn>();
  const [previousItem, setPreviousItem] = useState<IMegaMenuNavigationItemColumn>();
  const [suppressPath, setSuppressPath] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLUListElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [markerStyle, setMarkerStyle] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });

  const updateMarkerPosition = useCallback((index: number) => {
    if (navRef.current) {
      const items = navRef.current.querySelectorAll('li[data-nav-item="true"]');
      const targetItem = items[index] as HTMLElement;
      if (targetItem) {
        const navRect = navRef.current.getBoundingClientRect();
        const itemRect = targetItem.getBoundingClientRect();
        setMarkerStyle({
          left: itemRect.left - navRect.left + NAV_ITEM_PADDING, // account for padding
          width: itemRect.width - NAV_ITEM_PADDING * 2, // account for padding
          opacity: 1,
        });
      }
    }
  }, []);

  const hideMarker = useCallback(() => {
    setMarkerStyle((prev) => ({ ...prev, opacity: 0 }));
  }, []);

  const setActiveKey = useCallback(
    (next: string) => {
      onActivePathChange?.(next);
    },
    [onActivePathChange],
  );

  const closeMenu = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setExpandedItem(undefined);
      setPreviousItem(undefined);
      setActiveIndex(null);
      hideMarker();
      setActiveKey('');
      setSuppressPath(false);
      setIsClosing(false);
      setIsTransitioning(false);
      closeTimerRef.current = null;
    }, 300); // Match fade out duration
  }, [setActiveKey, hideMarker]);

  const handleItemClick = useCallback(
    (index: number, item: IMegaMenuNavigationItemColumn) => {
      setSuppressPath(true);

      if (expandedItem && isSameByKey(expandedItem, item)) {
        // Close if same item clicked
        closeMenu();
        return;
      }

      // If switching between different items, use crossfade
      if (expandedItem) {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
        }
        if (transitionTimerRef.current) {
          clearTimeout(transitionTimerRef.current);
        }

        // Set the previous item for crossfade
        setPreviousItem(expandedItem);
        setIsTransitioning(true);

        // Immediately set new item
        setExpandedItem(item);
        setActiveKey(item._key || '');
        setActiveIndex(index);

        // Clean up after transition
        transitionTimerRef.current = setTimeout(() => {
          setPreviousItem(undefined);
          setIsTransitioning(false);
          transitionTimerRef.current = null;
        }, 300);

        setTimeout(() => {
          updateMarkerPosition(index);
        }, MARKER_ANIMATION_DELAY);
      } else {
        // Open new item (no previous item)
        setActiveKey(item._key || '');
        setActiveIndex(index);
        setExpandedItem(item);

        setTimeout(() => {
          updateMarkerPosition(index);
        }, MARKER_ANIMATION_DELAY);
      }
    },
    [expandedItem, closeMenu, setActiveKey, updateMarkerPosition],
  );

  const pathActiveIndex = useMemo(() => {
    if (suppressPath || !activePath) return null;

    const matchIndex = navigationItems.findIndex((item) => {
      const itemPath = getActivePathFromLink(item.link) || '';
      const isExternal = Boolean(item.link && (item.link.isOpenInNewTab || item.link.type === 'external'));
      return !isExternal && pathMatches(itemPath, activePath);
    });

    return matchIndex >= 0 ? matchIndex : null;
  }, [suppressPath, activePath, navigationItems]);

  useEffect(() => {
    if (suppressPath) return;

    if (pathActiveIndex !== null && pathActiveIndex !== activeIndex) {
      setActiveIndex(pathActiveIndex);
      setActiveKey(navigationItems[pathActiveIndex]?._key || '');
      updateMarkerPosition(pathActiveIndex);
    } else if (pathActiveIndex === null && activeIndex !== null) {
      setActiveIndex(null);
      hideMarker();
    }
  }, [pathActiveIndex, activeIndex, navigationItems, suppressPath, hideMarker, setActiveKey, updateMarkerPosition]);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) closeMenu();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };

    const handleScroll = () => {
      if (expandedItem) closeMenu();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [expandedItem, closeMenu]);

  useEffect(() => {
    const handleResize = () => {
      if (activeIndex !== null) {
        updateMarkerPosition(activeIndex);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
    };
  }, [activeIndex, updateMarkerPosition]);

  return (
    <nav ref={rootRef} className={cn('relative flex items-center', className)} role="navigation" aria-label="Main">
      <div className="relative">
        <ul
          ref={navRef}
          className="relative hidden md:flex items-center space-x-2 gap-2xs whitespace-nowrap nav-root"
          role="menubar"
          aria-label="Primary"
        >
          {navigationItems.map((item, index) => (
            <NavigationItem
              key={item._key ?? `nav-${index}`}
              item={item}
              index={index}
              isActive={activeIndex === index}
              onItemClick={handleItemClick}
            />
          ))}
        </ul>

        <div
          className="pointer-events-none absolute -bottom-[0.5px] me-0 rounded-full h-[3px] bg-primary transition-[left,width,opacity] duration-300 ease-out z-51"
          style={{
            left: `${markerStyle.left}px`,
            width: `${markerStyle.width}px`,
            opacity: markerStyle.opacity,
          }}
          aria-hidden="true"
        />

        {(expandedItem || previousItem) && (
          <div className="absolute left-0 top-full w-screen z-50" role="region" aria-label="Sub menu">
            {/* Previous item - fading out */}
            {previousItem && isTransitioning && (
              <ListOrGridMegaMenu key={`previous-${previousItem._key}`} expandedItem={previousItem} isClosing={true} />
            )}

            {/* Current item - fading in or visible */}
            {expandedItem && (
              <ListOrGridMegaMenu
                key={`expanded-${expandedItem._key}`}
                expandedItem={expandedItem}
                isClosing={isClosing}
              />
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

const ListOrGridMegaMenu = ({
  expandedItem,
  isClosing,
}: {
  expandedItem: IMegaMenuNavigationItemColumn;
  isClosing: boolean;
}) => {
  const { hasColumnChildren, hasGridChildren } = useMemo(() => {
    return {
      hasColumnChildren: Boolean(
        (expandedItem?.childrenColumn?.length || 0) > 0 && expandedItem?.layoutType === 'column',
      ),
      hasGridChildren: Boolean((expandedItem?.childrenGrid?.length || 0) > 0 && expandedItem?.layoutType === 'grid'),
    };
  }, [expandedItem]);

  return (
    <div className="absolute inset-0">
      {hasColumnChildren ? (
        <ListMegaMenu
          key={expandedItem._key}
          items={expandedItem.childrenColumn || []}
          adBanner={expandedItem?.advertisingBanner?.isEnabled ? expandedItem.advertisingBanner : undefined}
          isClosing={isClosing}
        />
      ) : hasGridChildren ? (
        <GridMegaMenu key={expandedItem._key} items={expandedItem.childrenGrid || []} isClosing={isClosing} />
      ) : null}
    </div>
  );
};
